import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BookOpen, Upload, FileSpreadsheet, Loader2, Link2, Search, Building2,
  Receipt, ArrowDownToLine, ArrowUpFromLine, AlertCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

const INVOICE_STATUS = {
  draft:           'border-slate-500/40 text-slate-300 bg-slate-500/10',
  sent:            'border-sky-500/40 text-sky-200 bg-sky-500/10',
  paid:            'border-emerald-500/40 text-emerald-200 bg-emerald-500/10',
  partially_paid:  'border-amber-500/40 text-amber-200 bg-amber-500/10',
  overdue:         'border-rose-500/40 text-rose-200 bg-rose-500/10',
  void:            'border-slate-500/40 text-slate-400 bg-slate-500/10',
};

async function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = reader.result || '';
      resolve({ filename: file.name, data_base64: String(raw).split(',')[1] || '' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImportButton({ endpoint, label, onImported, disabled }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/\.(csv|xlsx|xls)$/i.test(f.name)) { toast.error('Please upload a CSV or XLSX file'); return; }
    setBusy(true);
    try {
      const payload = await fileToB64(f);
      const r = await api.post(endpoint, payload);
      toast.success(`Imported ${r.data.inserted} new · ${r.data.updated} updated`);
      onImported && onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };
  return (
    <label className={`inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 hover:border-amber-500/50 bg-[#0f2240] px-3 py-2 text-xs text-white cursor-pointer transition ${disabled || busy ? 'opacity-50 pointer-events-none' : ''}`}>
      <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onPick} disabled={disabled || busy} data-testid={`books-import-${label.toLowerCase()}`} />
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import {label}
    </label>
  );
}

// ---------- Unmatched customers dialog ----------
function LinkCustomerDialog({ open, onOpenChange, customer, clients, onLinked }) {
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setClientId(''); }, [open]);
  const submit = async () => {
    if (!clientId) { toast.error('Pick a CRM Client'); return; }
    setBusy(true);
    try {
      const r = await api.post('/books/unmatched-customers/link', {
        zoho_customer_id: customer?.zoho_customer_id || '',
        customer_name: customer?.customer_name || '',
        client_id: clientId,
      });
      toast.success(`Linked · ${r.data.invoices_linked} invoice(s) + ${r.data.payments_linked} payment(s)`);
      onLinked && onLinked();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Link2 className="h-4 w-4 text-amber-300" /> Link to CRM Client</DialogTitle></DialogHeader>
        <div className="py-2 space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Zoho customer</div>
            <div className="text-white font-semibold">{customer?.customer_name}</div>
            <div className="text-xs text-slate-400">
              {customer?.invoice_count} invoice(s) · {fmtINR(customer?.total_amount || 0)}
            </div>
          </div>
          <div>
            <Label>Pick a matching CRM Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-[#0f2240] border-amber-500/20 text-white" data-testid="link-client-trigger"><SelectValue placeholder="Search clients…" /></SelectTrigger>
              <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-80">
                {(clients || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company || c.name} <span className="text-slate-500 text-xs">· {c.email || c.phone || ''}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={!clientId || busy} data-testid="link-client-confirm" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Link customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BooksPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm('finance_admin');

  const [tab, setTab] = useState('invoices');
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [expenseSource, setExpenseSource] = useState('_all');
  const [linkCustomer, setLinkCustomer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const expParams = new URLSearchParams();
      if (q) expParams.set('q', q);
      if (expenseSource !== '_all') expParams.set('source', expenseSource);
      const calls = [
        api.get('/books/summary'),
        api.get(`/books/invoices?${params}`),
        api.get(`/books/payments?${params}`),
        api.get(`/books/expenses?${expParams}`),
      ];
      if (canManage) {
        calls.push(api.get('/books/unmatched-customers'));
        calls.push(api.get('/clients'));
      }
      const [s, i, p, e, u, c] = await Promise.all(calls);
      setSummary(s.data);
      setInvoices(i.data || []);
      setPayments(p.data || []);
      setExpenses(e.data || []);
      if (canManage) {
        setUnmatched(u.data || []);
        setClients(c.data || []);
      }
    } catch (_e) {
      setInvoices([]); setPayments([]); setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, expenseSource, canManage]);

  const exportExpenses = async (source) => {
    try {
      const params = new URLSearchParams();
      if (source) params.set('source', source);
      const resp = await api.get(`/books/expenses/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `zoho_expense_import_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Zoho-format expense export downloaded');
    } catch (_e) {
      toast.error('Export failed');
    }
  };

  return (
    <div>
      <PageHeader
        icon={BookOpen}
        title="Books"
        subtitle="Manual bridge to Zoho Books — import from Zoho, view here, export CRM-approved expenses back to Zoho format for your accountant."
        actions={canManage ? (
          <Button onClick={() => exportExpenses('crm_pushed')} data-testid="books-export-crm-pushed" className="bg-emerald-500 hover:bg-emerald-400 text-[#0a1628] font-semibold">
            <ArrowUpFromLine className="h-4 w-4 mr-1.5" /> Export CRM → Zoho
          </Button>
        ) : null}
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="shine-card p-3 rounded-xl">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Invoices</div>
            <div className="text-2xl font-extrabold text-white" data-testid="books-stat-invoices">{summary.invoices}</div>
          </Card>
          <Card className="shine-card p-3 rounded-xl">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Payments</div>
            <div className="text-2xl font-extrabold text-white" data-testid="books-stat-payments">{summary.payments}</div>
          </Card>
          <Card className="shine-card p-3 rounded-xl">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Bills / Expenses</div>
            <div className="text-2xl font-extrabold text-white" data-testid="books-stat-expenses">{summary.expenses}</div>
          </Card>
          <Card className="shine-card p-3 rounded-xl border-amber-500/40">
            <div className="text-[10px] uppercase tracking-wider text-amber-300">Outstanding</div>
            <div className="text-2xl font-extrabold text-white" data-testid="books-stat-outstanding">{fmtINR(summary.total_outstanding)}</div>
            {summary.unmatched_customers > 0 && (
              <div className="text-[10px] text-amber-300 mt-0.5">{summary.unmatched_customers} unmatched customer(s)</div>
            )}
          </Card>
        </div>
      )}

      <Card className="shine-card p-3 rounded-xl mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice #, customer, vendor…" className="pl-7 bg-[#0f2240] border-amber-500/20 text-white h-9" data-testid="books-search" />
          </div>
          {canManage && (
            <div className="flex items-center gap-2 flex-wrap">
              <ImportButton endpoint="/books/invoices/import" label="Invoices" onImported={load} />
              <ImportButton endpoint="/books/payments/import" label="Payments" onImported={load} />
              <ImportButton endpoint="/books/expenses/import" label="Expenses" onImported={load} />
            </div>
          )}
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab} data-testid="books-tabs">
        <TabsList className="bg-[#0a1628] border border-amber-500/20 flex-wrap h-auto">
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <Receipt className="h-4 w-4 mr-1.5" /> Invoices ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <ArrowDownToLine className="h-4 w-4 mr-1.5" /> Payments ({payments.length})
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bills / Expenses ({expenses.length})
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="unmatched" data-testid="tab-unmatched" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200 text-slate-300">
              <AlertCircle className="h-4 w-4 mr-1.5" /> Unmatched ({unmatched.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
              : invoices.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">
                  No invoices yet. {canManage ? 'Import a Zoho CSV/XLSX using the button above.' : 'Ask an Admin to import a Zoho export.'}
                </div>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="books-invoices-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-amber-500/10">
                        <th className="py-2 pr-3">Invoice #</th>
                        <th className="py-2 pr-3">Customer</th>
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3">Due</th>
                        <th className="py-2 pr-3 text-right">Amount</th>
                        <th className="py-2 pr-3 text-right">Balance</th>
                        <th className="py-2 pr-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-500/5">
                      {invoices.map((r) => (
                        <tr key={r.id} className="hover:bg-amber-500/5" data-testid={`books-invoice-${r.id}`}>
                          <td className="py-2 pr-3 text-white font-medium">{r.invoice_number}</td>
                          <td className="py-2 pr-3 text-slate-200 truncate max-w-[220px]">
                            {r.customer_name}
                            {!r.matched_client_id && <span className="ml-1 text-[10px] text-amber-300">· unmatched</span>}
                          </td>
                          <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{fmtDate(r.invoice_date)}</td>
                          <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{fmtDate(r.due_date)}</td>
                          <td className="py-2 pr-3 text-right text-white font-semibold whitespace-nowrap">{fmtINR(r.amount)}</td>
                          <td className="py-2 pr-3 text-right text-amber-200 font-semibold whitespace-nowrap">{fmtINR(r.balance_due)}</td>
                          <td className="py-2 pr-3"><Badge variant="outline" className={`${INVOICE_STATUS[r.status] || 'border-slate-500/40 text-slate-300'} text-[11px]`}>{r.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
              : payments.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">No payments imported yet.</div>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="books-payments-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-amber-500/10">
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3">Customer</th>
                        <th className="py-2 pr-3">Reference</th>
                        <th className="py-2 pr-3">Invoices</th>
                        <th className="py-2 pr-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-500/5">
                      {payments.map((r) => (
                        <tr key={r.id} className="hover:bg-amber-500/5">
                          <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{fmtDate(r.payment_date)}</td>
                          <td className="py-2 pr-3 text-slate-200 truncate max-w-[220px]">
                            {r.customer_name}
                            {!r.matched_client_id && <span className="ml-1 text-[10px] text-amber-300">· unmatched</span>}
                          </td>
                          <td className="py-2 pr-3 text-slate-400 truncate max-w-[180px]">{r.reference_number || '—'}</td>
                          <td className="py-2 pr-3 text-slate-400 truncate max-w-[180px]">{(r.invoice_numbers || []).join(', ') || '—'}</td>
                          <td className="py-2 pr-3 text-right text-white font-semibold whitespace-nowrap">{fmtINR(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </TabsContent>

        <TabsContent value="expenses" className="mt-4">
          <Card className="shine-card p-5 rounded-xl">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Label className="text-slate-400 text-xs">Show:</Label>
              <Select value={expenseSource} onValueChange={setExpenseSource}>
                <SelectTrigger className="w-40 h-8 bg-[#0f2240] border-amber-500/20 text-white text-xs" data-testid="books-expense-source"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20">
                  <SelectItem value="_all">All sources</SelectItem>
                  <SelectItem value="zoho">Imported from Zoho</SelectItem>
                  <SelectItem value="crm_pushed">Pushed from CRM (awaiting export)</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => exportExpenses('')} className="h-8 border-amber-500/20 text-white hover:bg-amber-500/10" data-testid="books-export-all">
                  <ArrowUpFromLine className="h-3.5 w-3.5 mr-1" /> Export all as Zoho format
                </Button>
              </div>
            </div>
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
              : expenses.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">No expenses recorded here yet.</div>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="books-expenses-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-amber-500/10">
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3">Vendor</th>
                        <th className="py-2 pr-3">Category</th>
                        <th className="py-2 pr-3 text-right">Amount</th>
                        <th className="py-2 pr-3">Source</th>
                        <th className="py-2 pr-3">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-500/5">
                      {expenses.map((r) => (
                        <tr key={r.id} className="hover:bg-amber-500/5">
                          <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{fmtDate(r.expense_date)}</td>
                          <td className="py-2 pr-3 text-white truncate max-w-[220px]">{r.vendor_name || '—'}</td>
                          <td className="py-2 pr-3 text-slate-300">{r.category || '—'}</td>
                          <td className="py-2 pr-3 text-right text-white font-semibold whitespace-nowrap">{fmtINR(r.amount)}</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className={r.source === 'crm_pushed'
                              ? 'border-amber-500/40 text-amber-200 bg-amber-500/10 text-[10px]'
                              : 'border-sky-500/40 text-sky-200 bg-sky-500/10 text-[10px]'}>
                              {r.source === 'crm_pushed' ? 'CRM → Zoho' : 'Zoho'}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-slate-400 italic truncate max-w-[240px]">{r.note || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="unmatched" className="mt-4">
            <Card className="shine-card p-5 rounded-xl">
              <h3 className="text-white font-bold mb-1 flex items-center gap-2"><AlertCircle className="h-4 w-4 text-amber-300" /> Zoho customers not linked to a CRM Client</h3>
              <p className="text-xs text-slate-400 mb-3">Auto-matching couldn't find a Client with the same email/phone. Link them here so their invoices show up under the right Client profile.</p>
              {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
                : unmatched.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">Every imported Zoho customer is already linked. Nothing to reconcile.</div>
                : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-amber-500/10">
                          <th className="py-2 pr-3">Zoho customer</th>
                          <th className="py-2 pr-3">Email</th>
                          <th className="py-2 pr-3">Phone</th>
                          <th className="py-2 pr-3 text-right">Invoices</th>
                          <th className="py-2 pr-3 text-right">Total</th>
                          <th className="py-2 pr-3 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-500/5">
                        {unmatched.map((r, idx) => (
                          <tr key={r.zoho_customer_id || r.customer_name || idx} className="hover:bg-amber-500/5" data-testid={`books-unmatched-${idx}`}>
                            <td className="py-2 pr-3 text-white font-medium truncate max-w-[240px]">{r.customer_name || '—'}</td>
                            <td className="py-2 pr-3 text-slate-400 truncate max-w-[220px]">{r.email || '—'}</td>
                            <td className="py-2 pr-3 text-slate-400">{r.phone || '—'}</td>
                            <td className="py-2 pr-3 text-right text-slate-300">{r.invoice_count}</td>
                            <td className="py-2 pr-3 text-right text-white font-semibold whitespace-nowrap">{fmtINR(r.total_amount)}</td>
                            <td className="py-2 pr-3">
                              <Button size="sm" variant="outline" onClick={() => setLinkCustomer(r)} className="h-7 border-amber-500/20 text-amber-200 hover:bg-amber-500/10 text-[11px]" data-testid={`books-link-${idx}`}>
                                <Link2 className="h-3 w-3 mr-1" /> Link
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <LinkCustomerDialog
        open={!!linkCustomer}
        onOpenChange={(v) => !v && setLinkCustomer(null)}
        customer={linkCustomer}
        clients={clients}
        onLinked={load}
      />
    </div>
  );
}
